export function pickFirst(obj, keys) {
  if (!obj || typeof obj !== 'object') return null;
  for (const key of keys) {
    const value = obj[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return null;
}

export function fmtValue(value) {
  if (value === undefined || value === null || value === '') return '—';
  if (typeof value === 'number') {
    return Number.isInteger(value) ? String(value) : value.toFixed(2);
  }
  return String(value);
}

export function fmtTime(value) {
  if (value === undefined || value === null || value === '' || value === 0) return '—';
  const numericValue = typeof value === 'number'
    ? value
    : (typeof value === 'string' && /^\d{10,}$/.test(value.trim()) ? Number(value.trim()) : null);
  if (numericValue && numericValue > 1_000_000_000) {
    const ms = numericValue < 1e12 ? numericValue * 1000 : numericValue;
    return new Date(ms).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  }
  if (typeof value === 'string' && value.includes('T')) {
    const d = new Date(value);
    if (!isNaN(d)) return d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  }
  return String(value);
}

const _FYERS_STATUS_MAP = { 1: 'Cancelled', 2: 'Filled', 4: 'Transit', 5: 'Rejected', 6: 'Pending' };

function _normalizeStatus(raw) {
  if (raw === undefined || raw === null || raw === '') return null;
  if (typeof raw === 'number') return _FYERS_STATUS_MAP[raw] || String(raw);
  return raw;
}

function _isTerminalFillStatus(status) {
  const value = String(status || '').trim().toLowerCase();
  return value === 'complete' || value === 'completed' || value === 'filled' || value === 'executed' || value === 'traded';
}

function _isMasterTrustOrderRow(row) {
  return Boolean(row?.oms_order_id && row?.order_side && row?.order_entry_time);
}

export function normalizeOrderRow(row, index) {
  const sideRaw = pickFirst(row, ['transaction_type', 'transactiontype', 'order_side', 'side', 'action', 'buy_or_sell', 'trnsTp', 'trsTyp']);
  const side =
    sideRaw === 'B' ? 'BUY'
    : sideRaw === 'S' ? 'SELL'
    : sideRaw === 1 ? 'BUY'
    : sideRaw === -1 ? 'SELL'
    : sideRaw;

  const orderTypeRaw = pickFirst(row, ['order_type', 'ordertype', 'orderType', 'type', 'prcTp', 'ordTyp']);
  const orderType =
    orderTypeRaw === 'MKT' ? 'MARKET'
    : orderTypeRaw === 'L' ? 'LIMIT'
    : orderTypeRaw === 'SL' ? 'SL'
    : orderTypeRaw === 'SL-M' ? 'SLM'
    : orderTypeRaw === 1 ? 'LIMIT'
    : orderTypeRaw === 2 ? 'MARKET'
    : orderTypeRaw === 3 ? 'SL'
    : orderTypeRaw === 4 ? 'SLM'
    : orderTypeRaw;

  const status = _normalizeStatus(pickFirst(row, ['status', 'order_status', 'orderStatus', 'ordSt', 'sts', 'orderstatus']));
  const price = _isMasterTrustOrderRow(row)
    ? pickFirst(row, ['price', 'limitPrice', 'prc', 'lmPrc', 'average_price', 'averagePrice', 'average_trade_price', 'avgPrc', 'averageprice'])
    : _isTerminalFillStatus(status)
    ? pickFirst(row, ['average_price', 'averagePrice', 'average_trade_price', 'avgPrc', 'averageprice', 'price', 'limitPrice', 'prc', 'lmPrc'])
    : pickFirst(row, ['price', 'limitPrice', 'prc', 'lmPrc', 'average_price', 'averagePrice', 'average_trade_price', 'avgPrc', 'averageprice']);
  const formattedInstrumentName = typeof row?.formattedInstrumentName === 'string'
    ? row.formattedInstrumentName.trim()
    : null;

  return {
    id: pickFirst(row, ['order_id', 'orderid', 'oms_order_id', 'id', 'orderId', 'nestOrderNumber', 'nOrdNo', 'nstOID', 'ordID']) || `order-${index}`,
    orderId: pickFirst(row, ['order_id', 'orderid', 'oms_order_id', 'id', 'orderId', 'nestOrderNumber', 'nOrdNo', 'nstOID', 'ordID']),
    symbol: formattedInstrumentName || pickFirst(row, ['trading_symbol', 'tradingsymbol', 'symbol', 'tradingSymbol', 'cpName', 'dpName', 'trdSym', 'sym']),
    side: typeof side === 'string' ? side.toUpperCase() : side,
    product: pickFirst(row, ['product', 'productType', 'producttype', 'prod', 'prdType', 'prdCode']),
    type: orderType,
    qty: pickFirst(row, ['quantity', 'qty', 'order_qty', 'filledshares', 'totalquantity', 'totQty', 'reqQty']),
    filledQty: pickFirst(row, ['filled_quantity', 'filledQty', 'traded_quantity', 'tradedQty', 'fillshares', 'fldQty', 'filledshares']),
    price,
    status,
    time: pickFirst(row, ['order_timestamp', 'exchange_update_timestamp', 'orderDateTime', 'order_datetime', 'created_at', 'order_entry_time', 'exchange_time', 'flDtTm', 'uSec', 'updatetime', 'exchtime', 'ordEntTm', 'ordTim', 'rcvTim', 'epochTim']),
  };
}

export function fmtPnl(value) {
  if (value === undefined || value === null || value === '') return '—';
  const num = Number(value);
  if (isNaN(num)) return String(value);
  return num.toFixed(2);
}

export function computeUnrealizedPnl(row, ltp) {
  const netQty = Number(row?.netQty) || 0;
  const buyAvg = Number(row?.buyAvg) || 0;
  const sellAvg = Number(row?.sellAvg) || 0;
  const live = Number(ltp);
  const lotSize = Number(row?.lotSize ?? row?.lot_size);
  const pnlMultiplier = Number(row?.pnlMultiplier ?? row?.pnl_multiplier);
  const useMcxMultiplier = lotSize > 0 && pnlMultiplier > 0;
  if (netQty > 0) {
    const priceDiff = live - buyAvg;
    return useMcxMultiplier
      ? priceDiff * (Math.abs(netQty) / lotSize) * pnlMultiplier
      : priceDiff * netQty;
  }
  const priceDiff = sellAvg - live;
  return useMcxMultiplier
    ? priceDiff * (Math.abs(netQty) / lotSize) * pnlMultiplier
    : priceDiff * Math.abs(netQty);
}

export function normalizePositionRow(row, index) {
  // Total qty = filled + carried forward (Kotak camelCase + MasterTrust snake_case)
  const flBuy = Number(row?.flBuyQty ?? 0);
  const flSell = Number(row?.flSellQty ?? 0);
  const cfBuy = Number(row?.cfBuyQty ?? row?.cf_buy_quantity ?? 0);
  const cfSell = Number(row?.cfSellQty ?? row?.cf_sell_quantity ?? 0);
  const kotakNetQty = (flBuy || flSell || cfBuy || cfSell) ? (flBuy - flSell + cfBuy - cfSell) : null;

  // Generic broker buy/sell qty fallback
  const buyQty = Number(pickFirst(row, ['buy_quantity', 'buyQty', 'buyqty']) ?? 0);
  const sellQty = Number(pickFirst(row, ['sell_quantity', 'sellQty', 'sellqty']) ?? 0);
  const genericNetQty = buyQty || sellQty ? buyQty - sellQty : null;

  // Kotak: compute avg price from amounts if not directly available
  const totalBuyQty = flBuy + cfBuy;
  const totalBuyAmt = Number(row?.buyAmt ?? row?.buy_amount ?? 0) + Number(row?.cfBuyAmt ?? row?.cf_buy_amount ?? 0);
  const kotakAvgPrice = totalBuyQty > 0 ? totalBuyAmt / totalBuyQty : null;

  // Kotak: compute sell avg from amounts
  const totalSellQty = flSell + cfSell;
  const totalSellAmt = Number(row?.sellAmt ?? row?.sell_amount ?? 0) + Number(row?.cfSellAmt ?? row?.cf_sell_amount ?? 0);
  const kotakSellAvg = totalSellQty > 0 ? totalSellAmt / totalSellQty : null;
  const actualBuyAvg = Number(row?.actual_average_buy_price ?? 0) || null;
  const actualSellAvg = Number(row?.actual_average_sell_price ?? 0) || null;

  // Resolved fields
  const rBuyQty = buyQty || totalBuyQty || 0;
  const rSellQty = sellQty || totalSellQty || 0;
  const rBuyAvg = Number(pickFirst(row, ['average_buy_price', 'buy_price', 'day_buy_price', 'buyAvg', 'buyAvgPrice', 'avgBuyPrc', 'buyavgprice', 'avgnetprice']) || actualBuyAvg || kotakAvgPrice) || 0;
  const rSellAvg = Number(pickFirst(row, ['average_sell_price', 'sell_price', 'day_sell_price', 'sellAvg', 'sellAvgPrice', 'avgSellPrc', 'sellavgprice']) || actualSellAvg || kotakSellAvg) || 0;
  const rNetQty = Number(pickFirst(row, ['net_quantity', 'netQty', 'netqty', 'quantity', 'qty']) ?? kotakNetQty ?? genericNetQty) || 0;
  const rLtp = Number(pickFirst(row, ['ltp', 'last_price', 'lastPrice', 'close_price', 'upldPrc']) ?? 0);

  // Compute realized/unrealized PnL as fallback when broker doesn't provide them
  const squaredQty = Math.min(rBuyQty, rSellQty);
  const computedRealized = (rBuyAvg && rSellAvg && squaredQty) ? (rSellAvg - rBuyAvg) * squaredQty : null;
  const openQty = Math.abs(rNetQty);
  let computedUnrealized = null;
  if (openQty > 0 && rLtp > 0) {
    computedUnrealized = computeUnrealizedPnl({
      netQty: rNetQty,
      buyAvg: rBuyAvg,
      sellAvg: rSellAvg,
      lot_size: row?.lot_size,
      pnl_multiplier: row?.pnl_multiplier,
    }, rLtp);
  }

  const brokerPnl = pickFirst(row, ['pnl', 'pl']);
  const brokerNetAmountMtm = pickFirst(row, ['net_amount_mtm']);
  const isSquaredOff = rNetQty === 0 && rBuyQty > 0 && rSellQty > 0;
  const realizedPnl = isSquaredOff
    ? (brokerPnl ?? computedRealized)
    : (pickFirst(row, ['realized_mtm', 'realized_profit', 'realised', 'realizedPnl', 'bookedPL']) ?? computedRealized);
  const unrealizedPnl = isSquaredOff
    ? 0
    : (pickFirst(row, ['unrealised', 'unrealised_pnl', 'unrealized_profit', 'unrealizedPnl', 'unBookedPL', 'mtm']) ?? computedUnrealized);

  return {
    id: pickFirst(row, ['symbol', 'trading_symbol', 'tradingsymbol', 'instrument_token', 'trdSym', 'tok']) || `position-${index}`,
    symbol: pickFirst(row, ['trading_symbol', 'tradingsymbol', 'symbol', 'tradingSymbol', 'cpName', 'dpName', 'trdSym', 'sym']),
    exchange: pickFirst(row, ['exchange', 'segment', 'exSeg']),
    product: pickFirst(row, ['product', 'productType', 'producttype', 'prod_type', 'prd', 'prod']),
    buyQty: rBuyQty || null,
    buyAvg: rBuyAvg || null,
    sellQty: rSellQty || null,
    sellAvg: rSellAvg || null,
    netQty: rNetQty,
    ltp: rLtp || null,
    realizedPnl,
    unrealizedPnl,
    lotSize: pickFirst(row, ['lot_size', 'lotSize']),
    pnlMultiplier: pickFirst(row, ['pnl_multiplier', 'pnlMultiplier']),
    pnlLots: pickFirst(row, ['pnl_lots', 'pnlLots']),
    pnl: brokerPnl
      ?? (rNetQty === 0 ? brokerNetAmountMtm : null)
      ?? ((realizedPnl != null || unrealizedPnl != null) ? (Number(realizedPnl) || 0) + (Number(unrealizedPnl) || 0) : null),
  };
}

export function normalizeHoldingRow(row, index) {
  return {
    id: pickFirst(row, ['isin', 'trading_symbol', 'tradingsymbol', 'symbol', 'trdSym']) || `holding-${index}`,
    symbol: pickFirst(row, ['trading_symbol', 'tradingsymbol', 'symbol', 'trdSym', 'sym', 'company_name']),
    exchange: pickFirst(row, ['exchange', 'exchange_segment', 'exchangeSegment', 'exSeg', 'exch', 'exc', 'exchseg']),
    qty: pickFirst(row, [
      'quantity',
      'qty',
      'holding_quantity',
      'total_qty',
      'holdingquantity',
      'hldQty',
      'totQty',
      'freeQty',
      'dpQty',
    ]),
    collateralQty: pickFirst(row, [
      'collateral_quantity',
      'collateral_qty',
      'collateralQuantity',
      'collQty',
      'pledgedQty',
    ]),
    avgPrice: pickFirst(row, [
      'average_price',
      'averageprice',
      'avgPrice',
      'averagePrice',
      'cost_price',
      'avgPrc',
      'avgRate',
    ]),
    ltp: pickFirst(row, ['ltp', 'last_price', 'lastPrice', 'close', 'closingPrice', 'prevDayLtp', 'clsPrc', 'ltpPrc']),
    pnl: pickFirst(row, ['pnl', 'profit_and_loss', 'profitLoss', 'unrealisedGainLoss', 'day_change', 'dayChg', 'mtm']),
  };
}
